'use client';

import { Reorder, AnimatePresence, motion } from 'framer-motion';
import { Plus, Sparkles } from 'lucide-react';

import { usePageBuilderContext } from '../page-builder-context';
import { BlockWrapper } from './block-wrapper';
import { TextBlockEditor } from './text-block-editor';
import { ImageBlockEditor } from './image-block-editor';
import { VideoBlockEditor } from './video-block-editor';
import { GalleryBlockEditor } from './gallery-block-editor';
import type { ContentBlock } from '@/lib/types/page-builder';
import { cn } from '@/lib/utils';
import { BlockGuideAnimation } from '../onboarding/block-guide-animation';

// ============================================================================
// BLOCK RENDERER
// ============================================================================

function BlockRenderer({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'text':
      return <TextBlockEditor block={block} />;
    case 'image':
      return <ImageBlockEditor block={block} />;
    case 'video':
      return <VideoBlockEditor block={block} />;
    case 'gallery':
      return <GalleryBlockEditor block={block} />;
    default:
      return null;
  }
}

// ============================================================================
// EMPTY STATE
// ============================================================================

function EmptyState() {
  const { openInsertMenu } = usePageBuilderContext();

  return (
    <div className="border-2 border-dashed border-border rounded-xl p-8 md:p-12">
      <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
        {/* Left: CTA */}
        <div className="flex-1 text-center md:text-left space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto md:mx-0">
            <Sparkles className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Расскажите свою историю
            </h3>
            <p className="text-sm text-foreground-secondary">
              Добавьте текст, изображения, видео и галереи, чтобы показать ваш продукт во всей красе
            </p>
          </div>
          <button
            type="button"
            onClick={() => openInsertMenu()}
            className={cn(
              'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium',
              'bg-secondary text-secondary-foreground border border-border hover:bg-secondary/80',
              'transition-colors duration-150'
            )}
          >
            <Plus className="h-4 w-4" />
            Добавить блок
          </button>
          <p className="text-xs text-foreground-tertiary">
            Или нажмите <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">/</kbd> для быстрого добавления
          </p>
        </div>

        {/* Right: Animated guide */}
        <div className="hidden md:block">
          <BlockGuideAnimation />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ADD BLOCK BUTTON
// ============================================================================

function AddBlockButton() {
  const { openInsertMenu, formData } = usePageBuilderContext();

  if (formData.blocks.length === 0) return null;

  return (
    <div className="flex justify-center py-4">
      <button
        type="button"
        onClick={() => openInsertMenu()}
        className={cn(
          'group flex items-center gap-2 px-4 py-2 rounded-lg',
          'text-sm text-foreground-tertiary hover:text-foreground',
          'border border-transparent hover:border-border hover:bg-muted/50',
          'transition-all duration-200'
        )}
      >
        <Plus className="h-4 w-4 transition-transform group-hover:rotate-90" />
        <span>Добавить блок</span>
        <kbd className="ml-2 px-1.5 py-0.5 rounded bg-muted/50 font-mono text-[10px] text-foreground-tertiary">
          /
        </kbd>
      </button>
    </div>
  );
}

// ============================================================================
// BLOCK LIST
// ============================================================================

export function BlockList() {
  const { formData, reorderBlocks } = usePageBuilderContext();

  return (
    <div className="space-y-4">
      {formData.blocks.length === 0 ? (
        <EmptyState />
      ) : (
        <Reorder.Group
          axis="y"
          values={formData.blocks}
          onReorder={reorderBlocks}
          className=""
        >
          <AnimatePresence initial={false}>
            {formData.blocks.map((block, index) => (
              <BlockWrapper
                key={block.id}
                block={block}
                isFirst={index === 0}
                isLast={index === formData.blocks.length - 1}
              >
                <BlockRenderer block={block} />
              </BlockWrapper>
            ))}
          </AnimatePresence>
        </Reorder.Group>
      )}

      <AddBlockButton />
    </div>
  );
}
